import { Lock, Sparkles, ArrowRight, Check } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";

// Shown in place of a page whose module the tenant has not enabled.
// Fail-closed at the route level: even a deep link lands here.
const MODULE_INCLUDES = {
  eligibility_docs: [
    "Initial & recert eligibility review",
    "CTI + Face-to-Face documentation",
    "HOPE validation & timepoint cockpit",
  ],
  ambient_scribe: [
    "Ambient bedside capture",
    "AI visit notes & summaries",
    "Clinician-verified before the chart",
  ],
  audit_defense: [
    "ADR / TPE defense packets",
    "CoP survey readiness (42 CFR §418)",
    "One-click audit binder export",
  ],
  compliance_autopilot: [
    "Auto-generated CoP deadlines",
    "Credential tracking & reminders",
    "Census sync + daily digest",
  ],
};

export default function ModuleLockedPrompt({ module }) {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const includes = MODULE_INCLUDES[module?.key] || [];

  return (
    <div
      className="max-w-2xl mx-auto mt-8"
      data-testid="module-locked-prompt"
    >
      <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 px-8 py-10 text-center">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-64 h-40 rounded-full blur-3xl bg-cyan-500" />
          </div>
          <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 border border-white/15 mb-4">
            <Lock className="w-7 h-7 text-cyan-300" />
          </div>
          <p className="relative text-xs font-bold uppercase tracking-[0.2em] text-cyan-300 mb-2">
            Module not enabled
          </p>
          <h1 className="relative text-2xl font-bold text-white font-manrope">
            {module?.name || "This capability"}
          </h1>
          {module?.tagline && (
            <p className="relative text-sm text-slate-300 mt-2 max-w-md mx-auto">
              {module.tagline}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="bg-white dark:bg-slate-900 px-8 py-7">
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
            This capability is part of the{" "}
            <span className="font-semibold text-slate-900 dark:text-white">
              {module?.name}
            </span>{" "}
            module, which isn&apos;t switched on for your agency yet. Turn it on to unlock:
          </p>

          <ul className="space-y-2.5 mb-7">
            {includes.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                </span>
                <span className="text-slate-700 dark:text-slate-300">{item}</span>
              </li>
            ))}
          </ul>

          <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4 mb-6">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Every module runs on the same compliant platform base — HIPAA
              posture, clinician-verification gate, and audit trail stay on no
              matter which modules you add.
            </p>
          </div>

          {isSuperAdmin ? (
            <Button
              onClick={() => navigate("/app/network/modules")}
              className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white gap-2"
              data-testid="module-manage-btn"
            >
              <Sparkles className="w-4 h-4" />
              Manage modules & pricing
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <div className="text-center">
              <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">
                Want this module?
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Ask your network administrator to enable{" "}
                <span className="font-semibold">{module?.name}</span> for your agency.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
